import { useCallback, useEffect } from 'react';

import isEqual from 'lodash/isEqual';

import { AppModalsEnum } from '@app/enums';
import { commonUiStateSlice, useAppDispatch, useAppSelector } from '@app/state';

const getLocationData = (): { hash: string; modals: string[] } => {
    const hash = window.location.hash.slice(1);
    const modals = hash ? hash.split(',') : [];
    return { hash, modals };
};

const useHashChangeListener = (onHashChange: () => void, fireOnLoad?: boolean) => {
    useEffect(() => {
        if (fireOnLoad) {
            // Fire once on page load
            onHashChange();
        }
    }, []);

    useEffect(() => {
        window.addEventListener('hashchange', onHashChange);

        return () => {
            window.removeEventListener('hashchange', onHashChange);
        };
    }, [onHashChange]);
};

export const useModalCloseOnBack = (modalName: AppModalsEnum, isModalOpen: boolean) => {
    const dispatch = useAppDispatch();

    const closeOnHashChange = useCallback(() => {
        const { modals } = getLocationData();

        setTimeout(() => {
            if (isModalOpen && !modals.some(modal => modal.startsWith(modalName))) {
                if (sessionStorage.getItem(modalName)) {
                    sessionStorage.removeItem(modalName);
                }
                dispatch(commonUiStateSlice.actions.resetModalDataAction(modalName));
            }
        }, 0);
    }, [isModalOpen, modalName]);

    useHashChangeListener(closeOnHashChange);
};

export const useModalFragmentSync = (
    modalName: AppModalsEnum,
    isModalOpen: boolean,
    onOpen?: () => void,
) => {
    const dispatch = useAppDispatch();

    useModalCloseOnBack(modalName, isModalOpen);

    const openOnHashChange = useCallback(() => {
        const { hash } = getLocationData();
        setTimeout(() => {
            if (!isModalOpen && hash.includes(modalName)) {
                dispatch(
                    commonUiStateSlice.actions.setModalDataAction({
                        modalName,
                        modalData: true,
                    }),
                );

                onOpen?.();
            }
        }, 0);
    }, [isModalOpen, modalName, onOpen]);

    useHashChangeListener(openOnHashChange, true);
};

export const useModalFragmentSyncWithData = <T>(
    modalName: AppModalsEnum,
    modalDataParser: (...args: string[]) => T | null,
) => {
    const dispatch = useAppDispatch();

    const currentModalData = useAppSelector(
        commonUiStateSlice.selectors.selectModalData(modalName),
    );

    useModalCloseOnBack(modalName, !!currentModalData);

    const openOnHashChange = useCallback(() => {
        const { modals } = getLocationData();

        setTimeout(() => {
            const modalFragment = modals.find(m => m.startsWith(modalName));

            if (modalFragment) {
                const parts = modalFragment.split(':');
                let data: T | null = null;

                if (parts.length > 2) {
                    data = modalDataParser(parts[1], parts[2]);
                } else if (parts[1]) {
                    data = modalDataParser(parts[1]);
                }

                if (!isEqual(currentModalData, data)) {
                    dispatch(
                        commonUiStateSlice.actions.setModalDataAction({
                            modalName,
                            modalData: data,
                        }),
                    );
                }
            }
        }, 0);
    }, [modalName, currentModalData, modalDataParser]);

    useHashChangeListener(openOnHashChange, true);
};
